import React, { useState, useMemo, useEffect } from 'react';
import { AppBar, Toolbar, Typography, TextField, Container, IconButton, Badge, Box, Grid, Chip, Modal, Backdrop, Fade, CardMedia, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

import useStore from '../store/useStore';
import ProductCard from '../components/ProductCard';
import CartDrawer from '../components/CartDrawer';
import CheckoutModal from '../components/CheckoutModal';

const WHATSAPP_NUMBER = "59177858921";

export default function CatalogApp() {
  const navigate = useNavigate();
  const { user, logout, createOrder, fetchProducts, fetchProductDetails, products } = useStore(); 

  const [zoomImage, setZoomImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("Todos");
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [productDetails, setProductDetails] = useState(null);
  // --- NUEVA LÓGICA DE LOCALSTORAGE ---
  
  // 1. Inicializar el carrito desde localStorage (si existe y no ha expirado)
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('stiletto_cart');
    if (!savedCart) return [];

    try {
      const { items, expiry } = JSON.parse(savedCart);
      // Validar si la hora actual es menor a la de expiración
      if (new Date().getTime() > expiry) {
        localStorage.removeItem('stiletto_cart');
        return [];
      }
      return items;
    } catch (e) {
      return [];
    }
  });

  // 2. Efecto para guardar en localStorage cada vez que el carrito cambie
  useEffect(() => {
    if (cart.length === 0) {
      localStorage.removeItem('stiletto_cart');
      return;
    }

    const cartData = {
      items: cart,
      // Guardamos el timestamp de "ahora + 1 hora"
      expiry: new Date().getTime() + (1000 * 60 * 60) 
    };
    localStorage.setItem('stiletto_cart', JSON.stringify(cartData));
  }, [cart]);

  useEffect(() => { fetchProducts(); }, []);

  const CATEGORIES = useMemo(() => ["Todos", ...new Set(products.map(p => p.categoria))], [products]);

  const handleProductClick = async (product) => {
    setLoadingProduct(true);
    try {
      const details = await fetchProductDetails(product._id);
      setProductDetails(details);
      setZoomImage(product.imagenUrl);
    } catch (error) {
      console.error("Error fetching product details:", error);
    } finally {
      setLoadingProduct(false);
    }
  };

  // Lógica de Carrito (Podría irse a un Hook personalizado en el futuro)
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item._id === product._id);
      if (existing) return prev.map(item => item._id === product._id ? { ...item, quantity: item.quantity + 1 } : item);
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const updateQuantity = (id, delta) => {
    setCart(prev => prev.map(item => item._id === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item));
  };

  const cartTotal = cart.reduce((acc, item) => acc + (item.precio * item.quantity), 0);
  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);

  const filteredProducts = useMemo(() => {
    return products.filter(p => (
      (p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || p._id.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (category === "Todos" || p.categoria === category)
    ));
  }, [searchTerm, category, products]);

  const handleCheckoutTrigger = () => {
    if (!user) { navigate('/login'); return; }
    setIsCheckoutModalOpen(true);
  };

  const handleFinalOrder = async (deliveryData) => {
    if (isSubmitting) return; // Evita doble envío
    
    setIsSubmitting(true);
    try {
      const orderData = {
        cartItems: cart.map(item => ({ 
          productId: item._id, 
          cantidad: item.quantity 
        })),
        shippingInfo: {
          nombre: deliveryData.nombre,
          telefono: deliveryData.telefono,
          instrucciones: deliveryData.instrucciones,
          lat: deliveryData.coordenadas[0],
          lng: deliveryData.coordenadas[1]
        },
        total: cartTotal
      };

      // 1. Guardar en DB (esperamos la respuesta)
      const response = await createOrder(orderData);
      
      // Obtenemos el ID real generado por MongoDB para el mensaje
      const orderId = response._id.slice(-6).toUpperCase();

      // 2. Preparar mensaje con ID real y link de mapas
      const mapLink = `https://www.google.com/maps?q=${deliveryData.coordenadas[0]},${deliveryData.coordenadas[1]}`;
      
      let mensaje = `*PEDIDO #${orderId}*\n`;
      mensaje += `📍 *Ubicación:* ${mapLink}\n`;
      // ... resto del mensaje ...

      // 3. Abrir WhatsApp
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(mensaje)}`, '_blank');

      // 4. Limpiar estados
      setCart([]);
      setIsCartOpen(false);
      setIsCheckoutModalOpen(false);
      
    } catch (error) {
      alert("Hubo un problema al registrar tu pedido. Por favor intenta de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'white', pb: 10 }}>
      {/* Header */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #f0f0f0' }}>
        <Toolbar sx={{ justifyContent: 'space-between', gap: 2 }}>
          
          <Typography variant="h6" sx={{ display:"flex", alignItems: "center",  fontWeight: 800, color: '#333', cursor: 'pointer' }} onClick={() => navigate('/')}>
            <Box sx={{ display: 'flex', gap: 1, marginRight: 2 }}>
              <CardMedia
                component="img" 
                image={process.env.PUBLIC_URL + '/logo192.png'} 
                alt={"Logo Stiletto Shop"}
                sx={{ top: 0, left: "10px", width: '60px', height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.3s ease',
                  '&:hover': { transform: 'scale(1.05)' }
                }}
              />
            </Box>
            Stiletto<span style={{ color: '#F7D060' }}>SHOP</span>
          </Typography>

          <TextField
            variant="outlined" size="small" placeholder="Buscar..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ bgcolor: '#f5f5f5', borderRadius: 50, width: { xs: '120px', md: '300px' }, '& fieldset': { border: 'none' } }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ color: '#999', mr: 1 }} /> }}
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={() => setIsCartOpen(true)}>
              <Badge badgeContent={cartCount} color="warning"><ShoppingCartIcon /></Badge>
            </IconButton>
            {user ? (
                <>
                    <IconButton onClick={logout}><LogoutIcon color="error" /></IconButton>
                    <IconButton onClick={() => navigate("/admin")}><AdminPanelSettingsIcon color="error" /></IconButton>
                </>
            ) : (
                <IconButton onClick={() => navigate('/login')}><LoginIcon /></IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        {/* Categorías */}
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 3 }}>
          {CATEGORIES.map(cat => (
            <Chip 
              key={cat} label={cat} onClick={() => setCategory(cat)}
              sx={{ bgcolor: category === cat ? '#F7D060' : '#f5f5f5', fontWeight: 'bold' }}
            />
          ))}
        </Box>

        {/* Listado */}
        <Grid container spacing={2} style={{ justifyContent: 'center' }}>
          {filteredProducts.map(product => (
            <Grid item xs={6} sm={4} md={3} lg={2.4} key={product._id} 
              style={{ 
                display: 'flex', 
                width: '47%', 
                maxWidth: 250 
              }}>
              <ProductCard 
                product={product} 
                onAddToCart={addToCart} 
                onZoom={() => handleProductClick(product)} 
              />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Product Details Modal */}
      <Modal open={Boolean(zoomImage)} onClose={() => { setZoomImage(null); setProductDetails(null); }} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.9)' } }}>
        <Fade in={Boolean(zoomImage)}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', outline: 'none' }}>
            <img src={zoomImage} alt="Zoom" style={{ maxWidth: '95vw', maxHeight: '90vh', borderRadius: '4px' }} />
          </Box>
        </Fade>
      </Modal>

      {/* Componentes Extraídos */}
      <CartDrawer 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)} 
        cart={cart}
        onRemove={(id) => setCart(curr => curr.filter(i => i._id !== id))}
        onUpdateQty={updateQuantity}
        total={cartTotal}
        onCheckout={handleCheckoutTrigger}
      />

      <CheckoutModal 
        open={isCheckoutModalOpen} 
        onClose={() => setIsCheckoutModalOpen(false)}
        onConfirm={handleFinalOrder}
        total={cartTotal}
      />
    </Box>
  );
}